import {
  Apps,
  CloseOutlined,
  FormatIndentDecreaseOutlined,
  FormatIndentIncreaseOutlined,
} from '@mui/icons-material';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  IconButton,
  Modal,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { getColor } from '../../assets/theme';
import { useAppSelector } from '../../store';

import AccountingPeriodSelector from '@components/accounting-finance/accounting-period-selector';
import PrintQueue from '@components/print/print-queue';
import { LiveClock } from '@components/ui/live-clock';
import { StationSelector } from '@components/ui/pos-station-selector';
import { PropertySelector } from '@components/ui/property-selector';
import { selectSalesDateByPath } from '@store/selector/app.selector';
import { usePOSScreen } from '@utils/screen-size';
import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import { memo, useMemo } from 'react';
import Notifications from '../../components/notifications/notifications';
import AccountAvatar from '../../components/ui/account-avatar';
import useLayoutContext from '../../hooks/useLayoutContext';
import Dashboard from '../../pages/dashboard/dashboard.page';
import { dateFormat } from '../../utils/format';
import DashboardHeader from './dashboard.header';
import { useNavigate } from 'react-router-dom';
import useAppContext from '../../hooks/useAppContext';
import { useAppDispatch } from '../../store';
import { setPOSSelectedStationSlice } from '@modules/point-of-sale/slice/pos-stations.slice';

const ModuleDisplay = memo(() => {
  const { bgColor } = getColor();
  const { closeModal } = useLayoutContext();
  return (
    <Box sx={{ bgcolor: bgColor }}>
      <Stack direction='column'>
        <Box>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='center'
            gap={2}
            sx={{ p: { xs: 2, md: 4 } }}
          >
            <Box
              sx={{
                width: 30,
                aspectRatio: 1,
                display: { xs: 'none', md: 'block' },
              }}
            />
            <Stack direction='row' justifyContent='center' sx={{ flex: 1 }}>
              <DashboardHeader />
            </Stack>
            <IconButton
              sx={{
                bgcolor: 'secondary.main',
                color: 'secondary.contrastText',
                ':hover': {
                  bgcolor: 'secondary.contrastText',
                  color: 'secondary.main',
                },
              }}
              onClick={closeModal}
            >
              <CloseOutlined />
            </IconButton>
          </Stack>
        </Box>
        <Box
          sx={{
            height: 'calc(100dvh - 84.7px)',
            padding: '24px',
            bgcolor: bgColor,
            overflowY: 'auto',
            position: 'relative',
          }}
        >
          <Dashboard component />
        </Box>
      </Stack>
    </Box>
  );
});

const Header = () => {
  const navigate = useNavigate();
  const {
    white,
    bgColor,
    primary,
    offWhite,
    textColor,
    darkMode: isDarkMode,
  } = getColor();

  const mediumScreen = useMediaQuery('(max-width: 900px)');

  const {
    toggleSidebar,
    isSidebarOpen,
    isOpenModal,
    closeModal,
    // openModal
  } = useLayoutContext();

  const dispatch = useAppDispatch();

  const selectedProperty = useAppSelector(
    (state) => state.properties.selectedProperty,
  );

  const {
    genpar: { refetchGenpar },
  } = useAppContext();

  const pathname = window.location.pathname?.split('/')?.[1];

  const currentSalesDateState = useAppSelector((state) =>
    selectSalesDateByPath(state, pathname),
  );

  const isPOS1024p = usePOSScreen();

  const isPOS = pathname === 'point-of-sale';

  return (
    <>
      <AppBar
        id='header'
        position='sticky'
        sx={{
          boxShadow: 0,
          bgcolor: white,
          padding: { xs: '0 0 0 10px', sm: '0 24px' },
          height: '80px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          borderBottom: `1px solid ${white}`,
          borderTop: `1px solid ${white}`,
          backgroundImage: 'none',
          zIndex: 100,
          gap: 2,
        }}
        component='header'
      >
        {/* Column One */}
        <Stack
          direction='row'
          gap={{ xs: 1, sm: 2 }}
          sx={{
            alignItems: 'center',
            flex: 1,
          }}
        >
          <Tooltip title='Sidebar Opener'>
            <IconButton
              id='header-sidebar_opener'
              disableRipple
              sx={{
                border: 'unset',
                ':hover': {
                  bgcolor: 'unset',
                  color: primary,
                },
              }}
              onClick={toggleSidebar}
            >
              {isSidebarOpen ? (
                <FormatIndentIncreaseOutlined />
              ) : (
                <FormatIndentDecreaseOutlined />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title='Module Selector'>
            <IconButton
              id='header-module_selector'
              disableRipple
              sx={{
                border: 'unset',
                ':hover': {
                  bgcolor: 'unset',
                  color: primary,
                },
              }}
              onClick={() => {
                dispatch(setPOSSelectedStationSlice(undefined));
                refetchGenpar();
                navigate('');
              }}
            >
              <Apps />
            </IconButton>
          </Tooltip>

          {!mediumScreen && isPOS && (
            <Stack
              gap={1}
              direction='row'
              sx={{
                bgcolor: offWhite,
                p: '8px 12px',
                borderRadius: '360px',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Avatar
                sx={{
                  width: '24px',
                  height: '24px',
                  fontSize: '12px',
                  color: '#FFFFFF',
                  bgcolor: 'secondary.main',
                }}
              >
                {selectedProperty?.label?.split(' ')?.map((item) => item[0])}
              </Avatar>
              <Typography sx={{ color: textColor, whiteSpace: 'nowrap' }}>
                {selectedProperty?.label}
              </Typography>
            </Stack>
          )}

          {(pathname === 'back-office' || isPOS) && <StationSelector />}

          {pathname === 'accounting-and-finance' && (
            <AccountingPeriodSelector />
          )}

          {/* SALES DATE FO UI */}
          {!isPOS1024p &&
            currentSalesDateState &&
            pathname === 'front-office' && (
              <Tooltip title='Sales Date'>
                <Stack
                  gap={1}
                  direction='row'
                  sx={{
                    bgcolor: offWhite,
                    p: '8px 12px',
                    borderRadius: '360px',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ color: textColor, whiteSpace: 'nowrap' }}>
                    {moment(currentSalesDateState)
                      .startOf('day')
                      .format(dateFormat)}
                  </Typography>
                  <Chip
                    label={moment(currentSalesDateState)
                      .startOf('day')
                      .format('ddd')}
                    sx={{
                      bgcolor: primary,
                      color: '#FFF',
                      cursor: 'default',
                    }}
                  />
                </Stack>
              </Tooltip>
            )}
        </Stack>
        {/* Column Two */}
        <Stack
          direction='row'
          gap={2}
          sx={{
            justifyContent: 'flex-end',
            alignSelf: 'stretch',
            alignItems: 'center',
            flex: 1,
          }}
        >
          {/* SALES DATE POS UI */}
          {!isPOS1024p && currentSalesDateState && isPOS && (
            <Tooltip title='Sales Date'>
              <Stack
                gap={1}
                direction='row'
                sx={{
                  bgcolor: offWhite,
                  p: '8px 12px',
                  borderRadius: '360px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ color: textColor, whiteSpace: 'nowrap' }}>
                  {moment(currentSalesDateState)
                    .startOf('day')
                    .format(dateFormat)}
                </Typography>
                <LiveClock />
                <Chip
                  label={moment(currentSalesDateState)
                    .startOf('day')
                    .format('ddd')}
                  sx={{
                    bgcolor: primary,
                    color: '#FFF',
                    cursor: 'default',
                  }}
                />
              </Stack>
            </Tooltip>
          )}
          {!mediumScreen && !isPOS && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <PropertySelector
                sx={{
                  width: '350px',
                  backgroundColor: bgColor,
                }}
                initialValue={selectedProperty}
              />
            </Box>
          )}

          {isPOS && <PrintQueue />}

          {pathname === 'front-office' && (
            <Notifications color={isDarkMode ? '#FFF' : '#161C248F'} />
          )}

          <AccountAvatar />
        </Stack>
      </AppBar>

      {useMemo(
        () => (
          <AnimatePresence>
            <Modal
              sx={{ zIndex: 1000 }}
              open={isOpenModal}
              onClose={closeModal}
            >
              <motion.div
                animate={{
                  scale: [0, 1],
                  opacity: [0, 1],
                }}
                transition={{
                  duration: 0.3,
                  ease: 'easeInOut',
                }}
                exit={{ scale: 0 }}
                style={{
                  position: 'absolute',
                  transform: 'translate(-50%, -50%)',
                  width: '100%',
                  height: '100%',
                }}
              >
                <ModuleDisplay />
              </motion.div>
            </Modal>
          </AnimatePresence>
        ),
        [isOpenModal],
      )}
    </>
  );
};

export default memo(Header);
